// Central date/time formatting shared by the web-ui SPA and the browser
// extension. One module so every surface renders timestamps identically - same
// locale, same timezone, same 12h/24h hour-cycle. (web-ui's lib/datetime.ts now
// re-exports this; the extension imports it directly.)
//
// Hour-cycle policy (the "12 vs 24h" overhaul):
//   'auto' - let the browser locale decide (en-US -> 1:30 PM, sv-SE -> 13:30)
//   '12h'  - force 12-hour regardless of locale
//   '24h'  - force 24-hour regardless of locale
//
// The signed-in user's saved preference (workspace_users.time_format, surfaced
// on /v1/api/me) drives it: each app calls configureClock() once the identity
// loads. Locale and timezone follow the browser (the user's actual device) - the
// faithful default - so only the hour-cycle is an explicit preference.

import { computed, ref } from 'vue';

export type ClockPref = 'auto' | '12h' | '24h';
export type TzPref = 'auto' | 'profile';

const browserLocale =
  typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

const clock  = ref<ClockPref>('auto');
const locale = ref<string>(browserLocale);

// Timezone preference: 'auto' = the browser's runtime zone (Intl default),
// 'profile' = the user's profile tz (resolved live from `profileTz`). When the
// profile tz is unset/invalid, 'profile' silently falls back to the browser.
const tzMode    = ref<TzPref>('auto');
const profileTz = ref<string | null>(null);

/** Set the active clock preference. Called by each app once the identity loads
 *  (and after a /me PATCH). Unknown values fall back to 'auto' so a stale/garbage
 *  server value can never break rendering. */
export function configureClock(pref: ClockPref | string | null | undefined): void {
  clock.value = pref === '12h' || pref === '24h' ? pref : 'auto';
}

/** The current clock preference (for the /me settings control). */
export function clockPref(): ClockPref {
  return clock.value;
}

function isValidZone(tz: string): boolean {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; }
  catch { return false; }
}

/** Set the timezone preference + the profile tz it resolves to. An invalid
 *  profile tz (bad IANA string) is treated as absent, so 'profile' falls back
 *  to the browser zone rather than throwing. */
export function configureTimezone(
  opts: { mode?: TzPref | string | null; profileTz?: string | null },
): void {
  tzMode.value = opts.mode === 'profile' ? 'profile' : 'auto';
  const tz = opts.profileTz?.trim() || null;
  profileTz.value = tz && isValidZone(tz) ? tz : null;
}

/** The current timezone preference (for the /me settings control). */
export function tzPref(): TzPref {
  return tzMode.value;
}

// hour12 to inject into Intl options. undefined => omit, so the locale decides.
const hour12 = computed<boolean | undefined>(() =>
  clock.value === '12h' ? true : clock.value === '24h' ? false : undefined,
);

// The IANA zone to render in, or undefined to use the browser runtime zone.
const effectiveTz = computed<string | undefined>(() =>
  tzMode.value === 'profile' && profileTz.value ? profileTz.value : undefined,
);

// Memoize formatters: constructing Intl.DateTimeFormat is the costly part, and
// these run in hot paths (lists, transcripts). Keyed by locale + hour-cycle +
// the option set.
const cache = new Map<string, Intl.DateTimeFormat>();
function formatter(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const h12 = hour12.value;
  const tz  = effectiveTz.value;
  const key = `${locale.value}|${h12}|${tz}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    const merged: Intl.DateTimeFormatOptions = {
      ...opts,
      ...(h12 === undefined ? {} : { hour12: h12 }),
      ...(tz ? { timeZone: tz } : {}),
    };
    f = new Intl.DateTimeFormat(locale.value, merged);
    cache.set(key, f);
  }
  return f;
}

function toDate(v: string | number | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Presets matching the option sets the views were hand-rolling.
export const DATETIME: Intl.DateTimeFormatOptions =
  { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
export const DATETIME_YEAR: Intl.DateTimeFormatOptions =
  { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
export const DATE: Intl.DateTimeFormatOptions =
  { year: 'numeric', month: 'short', day: 'numeric' };
export const DATE_SHORT: Intl.DateTimeFormatOptions =
  { month: 'short', day: 'numeric' };
export const TIME: Intl.DateTimeFormatOptions =
  { hour: 'numeric', minute: '2-digit' };

/** Date + time, e.g. "Jun 8, 1:30 PM" / "8 jun 2026 13:30". Returns the
 *  `empty` placeholder (default "-") for null/empty/invalid input. */
export function formatDateTime(
  v: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = DATETIME,
  empty = '-',
): string {
  const d = toDate(v);
  return d ? formatter(opts).format(d) : empty;
}

/** Date only, e.g. "Jun 8, 2026". */
export function formatDate(
  v: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = DATE,
  empty = '-',
): string {
  const d = toDate(v);
  return d ? formatter(opts).format(d) : empty;
}

/** Time only, e.g. "1:30 PM" / "13:30". */
export function formatTime(
  v: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = TIME,
  empty = '-',
): string {
  const d = toDate(v);
  return d ? formatter(opts).format(d) : empty;
}

/** Compact relative time for feeds: "just now", "5m", "3h", "2d", then an
 *  absolute date once older than a week (the exact day reads better than
 *  "9d"). Returns '' for invalid input. */
export function formatRelative(v: string | number | Date | null | undefined): string {
  const then = toDate(v);
  if (!then) return '';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return 'just now'; // clock skew / future
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return formatDate(then, DATE_SHORT, '');
}
